import {type ReactNode, forwardRef} from 'react'
import {Button, type ButtonProps} from '../button'
import {CircularProgress} from '../circular-progress'

// Intersection (not `interface extends`) because `ButtonProps` is a discriminated
// union (`cta` is contained-only) — an interface can't extend a union.
export type LoadingButtonProps = ButtonProps & {
  /** Where the loading spinner appears relative to the label. @default 'center' */
  loadingPosition?: 'start' | 'end' | 'center'
  /** Custom loading indicator. Defaults to a `<CircularProgress>`. */
  loadingIndicator?: ReactNode
}

/**
 * Button with explicit loading-indicator placement (MUI `LoadingButton`).
 * `start`/`end` keep the label visible with a spinner in that icon slot;
 * `center` (default) hides the content behind a centered spinner.
 */
export const LoadingButton = forwardRef<HTMLButtonElement, LoadingButtonProps>(
  (
    {
      loading,
      isLoading,
      loadingPosition = 'center',
      loadingIndicator,
      startIcon,
      endIcon,
      children,
      ...rest
    },
    ref,
  ) => {
    const busy = loading ?? isLoading
    // The ring inherits the button's current text color (white on contained,
    // colored on outlined/text, faint when the loading state disables it).
    const indicator = loadingIndicator ?? <CircularProgress size={16} color="inherit" />

    if (!busy) {
      return (
        <Button ref={ref} startIcon={startIcon} endIcon={endIcon} {...rest}>
          {children}
        </Button>
      )
    }

    if (loadingPosition === 'start') {
      return (
        <Button ref={ref} disabled aria-busy startIcon={indicator} endIcon={endIcon} {...rest}>
          {children}
        </Button>
      )
    }

    if (loadingPosition === 'end') {
      return (
        <Button ref={ref} disabled aria-busy startIcon={startIcon} endIcon={indicator} {...rest}>
          {children}
        </Button>
      )
    }

    // center
    return (
      <Button ref={ref} disabled aria-busy {...rest}>
        <span className="relative inline-flex items-center justify-center">
          <span className="invisible inline-flex items-center gap-2">
            {startIcon}
            {children}
            {endIcon}
          </span>
          <span className="absolute inset-0 flex items-center justify-center">{indicator}</span>
        </span>
      </Button>
    )
  },
)

LoadingButton.displayName = 'LoadingButton'
